/**
 * @file UploadTracker.js
 * @description Site-wide widget that appears while background asset uploads are
 * in progress, allowing users to keep navigating the app while files finish uploading.
 * It fades out shortly after the queue is cleared or dismissed.
 */
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useUploadContext, UploadStatus } from '../../../store/Contexts';
import { getText } from '../../../i18n';
import styles from './UploadTracker.module.scss';

const HIDE_TRANSITION_MS = 300;

const statusIconByStatus = {
    [UploadStatus.UPLOADING]: 'bi bi-cloud-upload',
    [UploadStatus.SUCCESS]: 'bi bi-check-circle-fill',
    [UploadStatus.ERROR]: 'bi bi-exclamation-circle-fill',
    [UploadStatus.CANCELLED]: 'bi bi-x-circle-fill',
};

const UploadRow = ({ upload, onCancel, onDismiss }) => {
    const isUploading = upload.status === UploadStatus.UPLOADING;

    return (
        <li className={styles.uploadRow}>
            <i
                className={classNames(
                    styles.uploadIcon,
                    styles[upload.status],
                    statusIconByStatus[upload.status],
                )}
            />
            <div className={styles.uploadDetails}>
                <span className={styles.uploadLabel}>{upload.label}</span>
                <div className={styles.progressTrack}>
                    <div
                        className={classNames(
                            styles.progressFill,
                            styles[upload.status],
                        )}
                        style={{ width: `${upload.progress}%` }}
                    />
                </div>
                {upload.status === UploadStatus.ERROR && (
                    <span className={styles.errorText}>
                        {upload.errorMessage}
                    </span>
                )}
                {upload.status === UploadStatus.CANCELLED && (
                    <span className={styles.errorText}>
                        {getText('msg_0fd24070fa4f')}
                    </span>
                )}
            </div>
            <button
                type="button"
                className={styles.actionButton}
                onClick={() =>
                    isUploading ? onCancel(upload.id) : onDismiss(upload.id)
                }
                aria-label={
                    isUploading
                        ? getText('msg_95f74facc41f')
                        : getText('msg_3a58815680a5')
                }
            >
                <i className="bi bi-x" />
            </button>
        </li>
    );
};

const resolveHeaderTitle = activeUploadCount =>
    activeUploadCount > 0
        ? getText('msg_922cc4ced026', { count: activeUploadCount })
        : getText('msg_c3dc1e389f0d');

const UploadTracker = () => {
    const { uploads, cancelUpload, dismissUpload, clearFinishedUploads } =
        useUploadContext();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isVisible, setIsVisible] = useState(uploads.length > 0);
    const [isClosing, setIsClosing] = useState(false);
    const hideTimeoutRef = useRef(null);

    const activeUploadCount = uploads.filter(
        upload => upload.status === UploadStatus.UPLOADING,
    ).length;
    const hasFinishedUploads = uploads.some(
        upload => upload.status !== UploadStatus.UPLOADING,
    );

    useEffect(() => {
        if (activeUploadCount > 0) {
            setIsCollapsed(false);
        }
    }, [activeUploadCount]);

    useEffect(() => {
        if (uploads.length > 0) {
            clearTimeout(hideTimeoutRef.current);
            setIsClosing(false);
            setIsVisible(true);
            return undefined;
        }

        if (!isVisible) {
            return undefined;
        }

        setIsClosing(true);
        hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false);
            setIsClosing(false);
        }, HIDE_TRANSITION_MS);

        return () => clearTimeout(hideTimeoutRef.current);
    }, [uploads.length, isVisible]);

    if (!isVisible) {
        return null;
    }

    return (
        <div
            className={classNames(styles.uploadTracker, {
                [styles.closing]: isClosing,
            })}
        >
            <div
                className={styles.header}
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <span className={styles.headerTitle}>
                    {resolveHeaderTitle(activeUploadCount)}
                </span>
                <div className={styles.headerActions}>
                    {hasFinishedUploads && (
                        <button
                            type="button"
                            className={styles.actionButton}
                            onClick={event => {
                                event.stopPropagation();
                                clearFinishedUploads();
                            }}
                            aria-label={getText('msg_70e87f2b7da3')}
                        >
                            <span className={styles.headerActions}>
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </button>
                    )}
                    <i
                        className={classNames(
                            'bi',
                            isCollapsed ? 'bi-chevron-up' : 'bi-chevron-down',
                        )}
                    />
                </div>
            </div>
            {!isCollapsed &&
                (uploads.length > 0 ? (
                    <ul className={styles.uploadList}>
                        {uploads.map(upload => (
                            <UploadRow
                                key={upload.id}
                                upload={upload}
                                onCancel={cancelUpload}
                                onDismiss={dismissUpload}
                            />
                        ))}
                    </ul>
                ) : (
                    <div className={styles.emptyState}>
                        {getText('msg_23bb06a78b39')}
                    </div>
                ))}
        </div>
    );
};

export default UploadTracker;
